import React, { useState } from 'react';
import { Droplet, Info, BarChart2, Award, Users, AlertTriangle } from 'lucide-react';

const WaterFootprintLanding = ({ onGetStarted }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [activeTab, setActiveTab] = useState('global');
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white font-sans">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Water Wave Animation Background */}
        <div className="absolute inset-0 z-0 opacity-20">
          <svg className="w-full h-full" viewBox="0 0 1440 320" preserveAspectRatio="none">
            <path 
              fill="#0099ff" 
              fillOpacity="1" 
              d="M0,32L48,80C96,128,192,224,288,224C384,224,480,128,576,90.7C672,53,768,75,864,96C960,117,1056,139,1152,149.3C1248,160,1344,160,1392,160L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            ></path>
          </svg>
        </div>
        
        <div className="container mx-auto px-4 py-12 md:py-20 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-bold text-blue-900 mb-4">
              Our Global Water Reality
            </h1>
            
            <p className="text-lg md:text-xl text-blue-700 mb-6">
              Understanding our water footprint is the first step toward sustainable water usage
            </p>
            
            {/* Top Call to Action Button */}
            <div 
              className="inline-block mb-8"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <button 
                onClick={onGetStarted}
                className={`
                  bg-blue-600 hover:bg-blue-700 text-white text-lg font-semibold 
                  py-3 px-6 rounded-full shadow-lg transform transition-all duration-300
                  ${isHovered ? 'scale-105' : 'scale-100'}
                `}
              >
                Calculate My Water Footprint
                {isHovered && (
                  <span className="ml-2 inline-block animate-bounce">→</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Water Proportion Visualization */}
      <div className="container mx-auto px-4 py-6 md:py-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-blue-900 text-center mb-6">
              Our Planet's Water Reality
            </h2>
            
            {/* Responsive layout: vertical on mobile, horizontal on larger screens */}
            <div className="flex flex-col lg:flex-row items-center justify-center space-y-8 lg:space-y-0 lg:space-x-6 mb-8">
              {/* Bucket - All World's Water */}
              <div className="flex flex-col items-center text-center max-w-xs">
                <div className="relative w-40 h-40 md:w-48 md:h-48 flex items-center justify-center mb-3">
                  <svg viewBox="0 0 288 288" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M149.873 34.6289C88.83 34.6289 39.375 50.8514 39.375 70.8539L57.7125 233.844C59.5125 253.621 95.67 279.001 149.895 279.001C204.12 279.001 239.827 255.219 242.077 233.844L260.415 70.8539C260.37 50.8514 210.893 34.6289 149.873 34.6289Z" fill="#03A9F4"/>
                    <path d="M259.811 58.0492C259.811 38.0467 211.639 23.5117 148.819 23.5117C94.9087 23.5117 48.9637 34.8517 37.2188 50.8942L40.5262 81.2467C40.5262 81.2467 65.1862 100.507 149.854 100.507C152.891 100.507 155.839 100.462 158.719 100.417L161.576 131.332H166.549L172.736 99.9442C239.111 96.5692 259.429 79.1542 259.429 79.1542L259.811 58.0492Z" fill="#1F6ABF"/>
                    <path d="M275.83 40.6781C275.83 40.6781 225.34 29.2031 148.278 29.2031C71.2153 29.2031 22.5703 39.2831 22.5703 39.2831C23.9878 55.8206 30.3778 70.4906 30.3778 70.4906C30.3778 70.4906 51.4378 92.9006 149.335 92.9006C247.233 92.9006 267.843 72.6956 270.858 69.3206C273.873 65.9006 275.83 40.6781 275.83 40.6781Z" fill="#03A9F4"/>
                    <path d="M275.652 35.8875C271.107 19.5525 226.287 9 149.202 9C72.117 9 27.297 19.5525 22.752 35.8875C18.6345 50.7375 58.077 66.8025 149.202 66.8025C240.327 66.8025 279.769 50.7375 275.652 35.8875Z" fill="#81D4FA"/>
                    <path d="M149.198 52.3139C208.397 52.3139 256.388 44.4061 256.388 34.6514C256.388 24.8967 207.199 22.5 148 22.5C88.8006 22.5 42.0078 24.8967 42.0078 34.6514C42.0078 44.4061 89.9984 52.3139 149.198 52.3139Z" fill="#44AAB5"/>
                    <path d="M186.681 120.938C159.073 136.53 88.3556 173.385 50.5781 170.415L53.9306 201.06C57.1706 201.555 132.006 203.265 196.851 127.597L186.681 120.938Z" fill="#1F6ABF"/>
                    <path d="M40.2818 78.6133C24.0593 94.6783 5.96933 114.726 14.8793 136.371C18.0968 144.178 24.8243 148.363 28.8743 150.163C28.8743 150.163 29.0093 148.633 29.3693 146.046C30.7193 136.101 35.7818 125.211 44.1743 113.218L40.2818 78.6133Z" fill="#757575"/>
                    <path d="M198.319 118.123C195.079 109.213 184.999 114.073 184.999 114.073C154.399 131.353 72.4989 171.223 32.4489 155.203C22.0089 151.04 13.3464 145.843 7.96887 131.78C7.92387 141.905 20.5689 170.57 30.9414 175.835C39.3564 180.088 49.0539 182.923 60.7314 182.923C80.4639 182.923 105.101 176.195 134.441 162.763C167.876 147.463 193.144 130.34 194.291 129.575C194.314 129.553 201.266 126.245 198.319 118.123Z" fill="#9E9E9E"/>
                    <path d="M194.291 108.92C189.701 105.432 181.916 107.052 175.458 110.382C172.871 111.327 152.463 124.85 119.186 137.855C75.9858 154.73 43.9458 157.61 26.5533 146.202C17.1033 139.992 13.5033 131.802 15.5733 121.092C18.0258 108.38 27.2733 94.8572 40.5483 81.1547L40.0308 76.6097L36.5433 74.7422C21.7833 89.5022 11.1183 104.825 8.26082 119.697C5.62832 133.31 10.5558 144.65 22.4808 152.457C31.2108 158.172 42.1908 160.512 54.3408 160.512C106.946 160.512 178.181 118.19 181.983 115.895C181.983 115.895 192.153 109.797 197.351 117.425C198.138 118.572 199.241 120.777 198.858 123.77C198.858 123.77 201.491 114.41 194.291 108.92Z" fill="#E0E0E0"/>
                  </svg>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-blue-800">All World's Water</h3>
                <div className="h-px w-16 bg-blue-200 my-2 mx-auto"></div>
                <p className="text-gray-600 text-sm md:text-base">If a bucket contained all the world's water...</p>
              </div>
              
              {/* Arrows: Different for mobile and desktop */}
              <div className="hidden lg:block transform rotate-0">
                <svg width="60" height="24" viewBox="0 0 60 24">
                  <path d="M5,12 L45,12 M35,4 L50,12 L35,20" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="block lg:hidden transform rotate-90">
                <svg width="24" height="24" viewBox="0 0 24 24">
                  <path d="M12,4 L12,20 M6,14 L12,20 L18,14" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              
              {/* Teacup - Freshwater */}
              <div className="flex flex-col items-center text-center max-w-xs">
                <div className="relative w-32 h-32 flex items-center justify-center mb-3">
                  <svg viewBox="0 0 366 366" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g opacity="0.57">
                      <path d="M319.673 95.0509L319.692 95.0482L312.779 275.985C312.65 279.471 311.562 282.876 309.533 285.711C299.474 299.716 277.065 307.157 246.282 311.339L246.267 311.341C215.49 315.522 191.912 314.327 178.478 303.514C175.766 301.322 173.813 298.33 172.749 295.006L117.821 122.47C117.531 121.666 117.32 120.847 117.206 120.009C114.691 101.493 157.958 80.3268 213.856 72.7338C257.082 66.862 295.196 70.8299 311.416 81.4911C316.172 84.6146 319.044 88.3152 319.614 92.5141C319.728 93.3519 319.747 94.1974 319.673 95.0509Z" fill="#F3F2E9"/>
                    </g>
                    <g opacity="0.6">
                      <path d="M288.501 282.481C295.225 279.421 299.656 272.842 299.9 265.458L302.942 173.2C303.004 172.126 302.93 170.602 302.176 169.086C299.281 163.263 288.884 163.16 268.254 163.168C241.467 163.178 241.068 163.312 237.96 162.682C224.52 159.957 212.699 153.528 208.115 151.035C197.614 145.325 197.005 143.087 190.062 141.33C178.829 138.486 174.731 142.903 167.642 138.806C160.405 134.624 161.958 128.449 154.248 124.539C149.681 122.223 141.867 120.702 137.943 124.248C131.098 130.431 138.549 149.8 143.281 162.1C143.946 163.83 146.414 170.09 149.88 179.376C150.936 182.204 151.792 184.531 152.353 186.064L153.228 188.661C153.235 188.674 153.235 188.678 153.235 188.678L182.461 281.811C184.639 288.75 190.512 293.924 197.688 295.108C222.695 299.233 262.238 294.434 288.501 282.481Z" fill="#65B1BB"/>
                    </g>
                    <path d="M217.32 315.553C198.977 315.554 185.961 312.098 177.226 305.066C174.264 302.676 172.059 299.407 170.85 295.611L115.934 123.115C115.597 122.186 115.361 121.232 115.231 120.279C113.89 110.405 123.662 99.8261 142.748 90.4903C161.187 81.4699 186.343 74.4649 213.579 70.7651C240.819 67.0652 266.932 67.1059 287.11 70.8804C307.995 74.7867 320.236 82.3756 321.577 92.249C321.695 93.1213 321.728 94.0156 321.673 94.9121C321.678 94.9832 321.679 95.0544 321.677 95.1262L314.764 276.062C314.611 280.044 313.358 283.782 311.141 286.873C301.549 300.228 281.024 308.629 246.546 313.312C235.58 314.802 225.869 315.553 217.32 315.553ZM252.61 71.996C240.449 71.996 227.426 72.8994 214.115 74.7076C187.288 78.3519 162.564 85.2261 144.496 94.0643C127.327 102.463 118.098 111.822 119.173 119.743C119.266 120.427 119.439 121.114 119.686 121.787C119.695 121.814 119.704 121.841 119.713 121.868L174.641 294.404C175.613 297.454 177.37 300.069 179.723 301.967C191.618 311.544 212.675 313.899 245.991 309.372C279.331 304.843 298.999 296.958 307.908 284.554C309.669 282.099 310.665 279.11 310.788 275.91L317.69 95.249C317.678 95.1306 317.676 95.0104 317.686 94.8899C317.745 94.1783 317.727 93.4699 317.634 92.7842C316.558 84.8639 305.166 78.3057 286.379 74.7915C276.417 72.9278 264.959 71.996 252.61 71.996Z" fill="#7F7F7F"/>
                    <path d="M239.486 78.9664C256.312 77.7318 289.596 76.4457 303.226 86.8275C306.273 89.2394 307.873 93.3006 306.303 97.0445C302.659 105.882 288.178 111.956 279.742 115.338C244.139 128.474 192.961 135.525 155.288 130.583C146.71 129.248 126.282 125.401 129.972 113.024C133.211 104.18 148.187 98.0003 156.474 94.8099C169.684 89.9115 183.355 86.6694 197.126 84.3025C197.667 84.2094 198.182 84.573 198.275 85.1145C198.366 85.6436 198.02 86.1469 197.5 86.2564C180.668 89.9659 163.767 94.4344 148.273 102.085C134.445 109.201 124.322 118.493 145.907 124.482C155.827 127.094 166.175 127.782 176.45 128.035C210.871 128.265 245.846 123.31 278.255 111.474C284.484 109.104 305.947 100.304 302.74 92.0556C299.702 86.1274 287.273 83.8625 281.004 82.7948C136.408 82.2779 134.487 80.9709 133.07 82Z" fill="#39AEBB"/>
                  </svg>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-blue-800">Freshwater</h3>
                <div className="h-px w-16 bg-blue-200 my-2 mx-auto"></div>
                <p className="text-gray-600 text-sm md:text-base">...one teacup of that would be freshwater...</p>
              </div>
              
              {/* Arrows: Different for mobile and desktop */}
              <div className="hidden lg:block transform rotate-0">
                <svg width="60" height="24" viewBox="0 0 60 24">
                  <path d="M5,12 L45,12 M35,4 L50,12 L35,20" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="block lg:hidden transform rotate-90">
                <svg width="24" height="24" viewBox="0 0 24 24">
                  <path d="M12,4 L12,20 M6,14 L12,20 L18,14" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              
              {/* Teaspoon - Available Freshwater */}
              <div className="flex flex-col items-center text-center max-w-xs">
                <div className="relative w-24 h-24 flex items-center justify-center mb-3">
                  <svg viewBox="0 0 291 299" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M285.57 10.1267C282.799 4.11367 116.634 97.2837 116.634 97.2837L36.4171 129.627L21.5781 140.936C21.5781 140.936 27.1411 163.534 67.6011 155.034C94.0831 149.47 102.81 127.529 112.48 113.307C120.046 102.18 122.609 101.419 127.019 98.6187C140.346 90.1577 164.23 78.8557 178.352 72.2947C194.82 64.6437 273.469 28.7967 280.721 23.1747C285.025 19.8387 288.342 16.1397 285.57 10.1267Z" fill="#7E8183"/>
                    <path d="M282.064 5.85549C279.329 4.11749 275.821 4.2885 272.786 4.9665C269.452 5.7115 266.186 6.87049 262.951 7.96349C256.65 10.0945 186.377 48.0985 175.224 55.4465C163.977 62.8565 123.152 89.8245 113.071 95.6525C105.694 99.0645 102.854 99.3155 96.4536 100.845C87.0756 103.087 79.6756 103.652 70.3286 106.012C61.8726 108.147 53.4876 110.815 45.6856 114.791C37.8766 118.771 30.7396 124.019 25.2696 130.975C23.7806 132.869 22.3546 134.915 21.7106 137.276C21.0246 139.79 21.5386 142.16 23.1526 144.184C25.8416 147.554 30.0116 149.606 34.0646 150.723C38.2536 151.878 42.6696 152.062 46.9846 151.809C51.4066 151.549 55.8176 150.816 60.1226 149.773C68.9786 147.627 77.4666 144.069 85.2246 139.246C93.0416 134.387 99.9826 128.253 105.889 121.163C112.196 113.592 112.153 108.109 120.043 102.181C148.083 81.1145 246.447 38.9795 261.58 30.5225C268.029 26.9185 273.746 24.2915 280.941 19.4505C283.315 17.8535 285.74 15.6035 285.902 12.5225C286.051 9.7185 284.337 7.29849 282.064 5.85549Z" fill="#B6B8BA"/>
                    <path opacity="0.65" d="M80.9709 133.07C79.4309 131.4 77.1159 130.685 74.9769 130.157C72.4799 129.542 69.9359 129.091 67.3849 128.763C56.9489 127.422 46.2689 128.363 36.2059 131.419C32.8549 132.437 29.1659 133.59 26.1339 135.565C24.5009 137.289 23.2939 139.345 22.6069 141.679C21.4679 145.55 21.4729 149.763 21.4349 153.763C21.4039 157.065 21.4999 160.364 21.6549 163.662C21.7399 165.194 21.8279 166.726 21.9029 168.258C22.2159 173.345 22.6189 178.43 22.9059 183.511C23.3979 192.221 24.9389 238.033 24.9959 242.067C25.0549 246.178 26.0049 262.705 25.9699 266.869C25.9529 268.939 25.8749 271.01 25.7089 273.074C25.5959 274.475 25.4369 275.886 24.9859 277.224C24.2259 279.478 22.6259 281.092 20.7189 282.44C20.0379 282.921 19.3429 283.373 18.6259 283.788C18.6319 283.886 18.5899 283.983 18.4679 284.038C17.3329 284.549 16.2019 285.068 15.0719 285.589C13.9499 286.106 12.8289 286.625 11.7059 287.14C10.8439 287.535 9.99285 287.967 9.09185 288.265C7.77285 288.702 6.37885 289.101 5.20685 289.871C4.52685 290.318 4.07185 290.971 4.59285 291.635C5.08385 292.261 5.95985 292.579 6.68785 292.822C9.00985 293.595 11.5209 293.842 13.9479 294.034C15.8339 294.183 17.7279 294.265 19.6209 294.28C21.4729 294.294 23.3239 294.373 25.1759 294.382C27.6459 294.395 30.1169 294.357 32.5849 294.257C37.4699 294.06 42.3299 293.597 47.1589 292.833C48.3679 292.642 49.5969 292.635 50.7719 292.25C51.1919 292.112 51.8039 291.886 51.9539 291.418C52.1149 290.915 51.5839 290.54 51.2229 290.3C50.0429 289.518 48.6469 289.085 47.3259 288.607C46.1359 288.177 44.9829 287.623 43.8219 287.118C41.0359 285.904 37.7379 284.372 35.3699 282.169C34.7069 281.785 34.1979 281.425 33.8859 281.096C29.2309 276.183 30.0869 262.359 30.0889 262.008C30.1159 255.572 29.9829 234.101 30.1119 229.006C30.2399 223.945 31.2029 193.75 31.6889 183.722C31.9319 178.711 32.2049 173.701 32.5229 168.694C32.7489 165.145 33.0569 161.384 34.7099 158.168C35.4459 156.735 36.4959 155.5 37.8699 154.649C39.6339 153.555 41.7279 153.146 43.7499 152.827C47.9059 152.172 52.0679 151.619 56.1889 150.759C64.0189 149.124 71.9319 146.712 78.3169 141.841C78.4269 141.886 78.5649 141.902 78.7429 141.869C80.5909 141.531 81.8388 140.046 82.3008 138.282C82.7948 136.408 82.2779 134.487 80.9709 133.07Z" fill="#39AEBB"/>
                  </svg>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-blue-800">Available Freshwater</h3>
                <div className="h-px w-16 bg-blue-200 my-2 mx-auto"></div>
                <p className="text-gray-600 text-sm md:text-base">...and just one teaspoon would be available for us to use.</p>
              </div>
            </div>
            
            <div className="text-center">
              <p className="text-base md:text-lg text-gray-700 max-w-2xl mx-auto">
                In theory, this is enough to meet all the daily, basic needs of all the people around the world. Yet whether you are able to access that water depends on who you are and where you live.
              </p>
            </div>
          </div>
          
          {/* Water Access Reality - Interactive Tabs */}
          <div className="bg-white rounded-xl shadow-xl p-6 md:p-8 mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-blue-900 text-center mb-4">
              The Reality of Water Access
            </h2>
            
            {/* Tab Navigation */}
            <div className="flex justify-center mb-4">
              <div className="inline-flex rounded-md shadow-sm" role="group">
                <button 
                  className={`px-4 py-2 text-sm font-medium border rounded-l-lg focus:z-10 focus:outline-none ${
                    activeTab === 'global' 
                      ? 'text-white bg-blue-600 border-blue-600' 
                      : 'text-blue-700 bg-white border-blue-300 hover:bg-blue-50'
                  }`}
                  onClick={() => setActiveTab('global')}
                >
                  Global Crisis
                </button>
                <button 
                  className={`px-4 py-2 text-sm font-medium border rounded-r-lg focus:z-10 focus:outline-none ${
                    activeTab === 'local' 
                      ? 'text-white bg-blue-600 border-blue-600' 
                      : 'text-blue-700 bg-white border-blue-300 hover:bg-blue-50'
                  }`}
                  onClick={() => setActiveTab('local')}
                >
                  Urban India
                </button>
              </div>
            </div>
            
            {/* Tab Content with smooth transitions */}
            <div className="relative overflow-hidden">
              {/* Global Water Access Tab */}
              <div 
                className={`transition-opacity duration-500 ${
                  activeTab === 'global' ? 'opacity-100' : 'opacity-0 absolute inset-0 pointer-events-none'
                }`}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Global Water Scarcity */}
                  <div>
                    <div className="flex items-start mb-4">
                      <AlertTriangle className="w-6 h-6 text-red-500 mr-3 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="text-lg font-bold text-blue-900 mb-2">Global Water Scarcity</h3>
                        <p className="text-gray-700 mb-3">
                          <span className="text-xl font-bold text-red-600">4 billion</span> people
                          — nearly <span className="font-semibold">two-thirds</span> of the world's population —
                          live in areas where for at least part of the year, demand exceeds supply.
                        </p>
                      </div>
                    </div>
                    
                    {/* Visualization of 2/3 population */}
                    <div className="bg-gray-100 rounded-lg p-3">
                      <div className="flex items-center mb-2">
                        <div className="w-full bg-gray-300 rounded-full h-5">
                          <div className="bg-red-500 h-5 rounded-full" style={{ width: '66%' }}></div>
                        </div>
                      </div>
                      <div className="text-sm text-center text-gray-600">
                        2/3 of global population faces water scarcity
                      </div>
                    </div>
                  </div>
                  
                  {/* Safe Drinking Water Access */}
                  <div>
                    <div className="flex items-start mb-4">
                      <Users className="w-6 h-6 text-blue-500 mr-3 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="text-lg font-bold text-blue-900 mb-2">Safe Drinking Water Access</h3>
                        <p className="text-gray-700 mb-3">
                          <span className="text-xl font-bold text-red-600">1 in 3</span> people
                          globally do not have access to safe drinking water.
                        </p>
                      </div>
                    </div>
                    
                    {/* Visualization of 1 in 3 people */}
                    <div className="bg-gray-100 rounded-lg p-3">
                      <div className="flex justify-center mb-2">
                        <div className="grid grid-cols-3 gap-1">
                          {[...Array(3)].map((_, i) => (
                            <div 
                              key={i} 
                              className={`w-12 h-12 rounded-full ${i === 0 ? 'bg-red-500' : 'bg-blue-400'} flex items-center justify-center text-white font-bold`}
                            >
                              {i === 0 ? '!' : ''}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="text-sm text-center text-gray-600">
                        1 in 3 people lack access to safe drinking water
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Indian Cities Tab */}
              <div 
                className={`transition-opacity duration-500 ${
                  activeTab === 'local' ? 'opacity-100' : 'opacity-0 absolute inset-0 pointer-events-none'
                }`}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Bangalore Water Level Drops */}
                  <div>
                    <div className="flex items-start mb-4">
                      <AlertTriangle className="w-6 h-6 text-red-500 mr-3 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="text-lg font-bold text-blue-900 mb-2">Bangalore Water Crisis</h3>
                        <p className="text-gray-700 mb-3">
                          Predicted groundwater levels are dropping rapidly, with an expected decline of 
                          <span className="text-xl font-bold text-red-600 mx-1">5-25 meters</span> 
                          by April 2025 from December 2024 levels.
                        </p>
                      </div>
                    </div>
                    
                    {/* Visualization of Water Level Drops */}
                    <div className="bg-gray-100 rounded-lg p-3">
                      <div className="relative h-36 mx-auto">
                        <div className="absolute bottom-0 left-0 w-full h-full flex">
                          <div className="flex-1 border-r border-gray-300 flex flex-col justify-end items-center">
                            <div className="bg-red-200 w-full" style={{ height: '15%' }}></div>
                            <div className="mt-2 text-xs font-semibold">Core Area<br/>5m drop</div>
                          </div>
                          <div className="flex-1 border-r border-gray-300 flex flex-col justify-end items-center">
                            <div className="bg-red-400 w-full" style={{ height: '40%' }}></div>
                            <div className="mt-2 text-xs font-semibold">CMC Area<br/>10-15m drop</div>
                          </div>
                          <div className="flex-1 flex flex-col justify-end items-center">
                            <div className="bg-red-600 w-full" style={{ height: '65%' }}></div>
                            <div className="mt-2 text-xs font-semibold">110 Villages<br/>20-25m drop</div>
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-center text-gray-600 mt-2">
                        Predicted water level drops by region (by April 2025)
                      </div>
                    </div>
                  </div>
                  
                  {/* Hyderabad Specific Data */}
                  <div>
                    <div className="flex items-start mb-4">
                      <BarChart2 className="w-6 h-6 text-blue-500 mr-3 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="text-lg font-bold text-blue-900 mb-2">Hyderabad's Paradox</h3>
                        <p className="text-gray-700 mb-3">
                          Despite receiving <span className="font-semibold">15% excess rainfall</span>, Hyderabad's groundwater levels 
                          have fallen by <span className="text-xl font-bold text-red-600">1.33 meters</span>.
                        </p>
                      </div>
                    </div>
                    
                    {/* Visualization of Hyderabad's Paradox */}
                    <div className="bg-gray-100 rounded-lg p-3">
                      <div className="flex items-center justify-center h-36">
                        <div className="w-16 h-full flex flex-col items-center justify-end mr-6">
                          <div className="bg-blue-400 w-full" style={{ height: '75%' }}></div>
                          <div className="w-full text-center mt-2 text-xs">Rainfall<br/>+15%</div>
                        </div>
                        <div className="w-16 h-full flex flex-col items-center justify-end">
                          <div className="relative w-full h-full flex flex-col items-center justify-end">
                            <div className="absolute top-1/2 border-t-2 border-dashed border-gray-400 w-full"></div>
                            <div className="bg-red-500 w-full" style={{ height: '40%' }}></div>
                            <div className="absolute text-xs font-bold text-red-600" style={{ top: '45%' }}>
                              -1.33m
                            </div>
                          </div>
                          <div className="w-full text-center mt-2 text-xs">Groundwater<br/>Level</div>
                        </div>
                      </div>
                      <div className="text-xs text-center text-gray-600 mt-2">
                        More rain but less groundwater: the urban water paradox
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Call to Action */}
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-blue-900 mb-4">
              Understand Your Water Footprint
            </h2>
            <p className="text-base md:text-lg text-gray-700 mb-6 max-w-2xl mx-auto">
              Knowing your water footprint is the first step toward making a difference in the global water crisis.
            </p>
            
            <div 
              className="inline-block"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <button 
                onClick={onGetStarted}
                className={`
                  bg-blue-600 hover:bg-blue-700 text-white text-base md:text-lg font-semibold 
                  py-3 px-6 rounded-full shadow-lg transform transition-all duration-300
                  ${isHovered ? 'scale-105' : 'scale-100'}
                `}
              >
                Calculate My Water Footprint
                {isHovered && (
                  <span className="ml-2 inline-block animate-bounce">→</span>
                )}
              </button>
            </div>
            
            <p className="mt-3 text-xs md:text-sm text-gray-500">
              Takes only 2 minutes • No sign-up required
            </p>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <div className="bg-blue-900 text-white py-6">
        <div className="container mx-auto px-4 text-center">
          <p className="mb-2 text-sm md:text-base">
            Every drop counts. Start your water conservation journey today.
          </p>
          <p className="text-xs text-blue-300">
            Data sources: UNESCO, UN Water, Water Footprint Network
          </p>
        </div>
      </div>
    </div>
  );
};

export default WaterFootprintLanding;